import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  bold,
  h1Style,
  h1Wrap,
  h2Style,
  headerTextStyle,
  imagePreview2,
  orderedList,
  imageGridTitle,
  largeImageGrid,
  imagePreviewBodyStyle,
  stepSectionStyle,
} from "./article.css"
import Footer from "~/components/Footer"
import Link from "~/components/Link"
import { golden } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import { btnWrap } from "./index.css"
import LinkButton from "~/components/LinkButton"
import StickyHeader from "~/components/StickyHeader"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    {
      title: "AI Yard Design | Revolutionising Outdoor Spaces with Rescape",
    },
    {
      name: "description",
      content:
        "Explore the future of landscaping with landscape design for backyard. Transform your outdoor space creating tailored, stunning AI yard design effortlessly.",
    },
    {
      property: "og:title",
      content: "Revolutionising Outdoor Spaces with AI Yard Design | Rescape AI",
    },
    {
      property: "og:description",
      content:
        "Explore the future of landscaping with Rescape’s AI backyard designer. Transform your outdoor space using AI to craft tailored, stunning ai yard design effortlessly.",
    },
    {
      property: "og:image",
      content:
        "https://rescapeai.com/rosegardens/e80f1046-70d1-4277-bf36-84e8c75091cf.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/ai-yard-design",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "AI Yard Design | Revolutionising Outdoor Spaces with Rescape",
        description:
          "Explore the future of landscaping with landscape design for backyard. Transform your outdoor space creating tailored, stunning AI yard design effortlessly.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-06-28",
        dateModified: "2024-06-28",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/articles/ai-yard-design-patio-and-flowerbeds.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/ai-yard-design",
        },
        articleSection: ["Gardening", "Artificial Intelligence", "Technology"],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/ai-yard-design-patio-and-flowerbeds.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>AI Yard Design: The Future of Landscaping</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Think of your backyard as an empty plot, a bit wild around the edges and
            waiting for its potential to be tapped. Once upon a time, you'd call in a
            landscaper or at least someone with a knack for design to morph that dull
            patch into something out of "Better Homes & Gardens."
          </p>
          <p className={articleBodyStyle}>
            Thanks to some clever AI tech advances, you can wave goodbye to those days of
            thumbing through portfolios for inspiration.
          </p>
          <p className={articleBodyStyle}>
            Rescape AI is here: where ones and zeros meet soil and water, transforming
            your garden dreams into stunning reality. No expensive landscaper is
            necessary.
          </p>
          <p className={articleBodyStyle}>
            With just your smartphone camera and Rescape AI, you will see your garden go
            from boring to spectacular right before your eyes.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>How Can AI Be Used in Yard Garden Planning?</h2>
          <p className={articleBodyStyle}>
            AI backyard design is like having a personal advisor who knows every inch of
            your yard and understands exactly what you want from it. Gone are the days
            when only those with years of experience or an innate knack for design could
            create stunning gardens.
          </p>
          <p className={articleBodyStyle}>
            <Link href="/garden-design-app">Garden design apps</Link> like Rescape remove
            the guesswork. It considers everything about your outdoor space, its size,
            shape, and even permanent features like sheds (or a favourite tree). Then, it
            matches the specs with your vision, whether that's blooming flower beds to
            entertain guests or a quiet green nook just for yourself.
          </p>
          <p className={articleBodyStyle}>
            This technology delivers a garden plan that feels right at home with your
            lifestyle while maximising every square foot of your outdoor haven. Whether
            you're new to gardening or have been nurturing plants for years, AI brings
            personalised landscaping insight directly into your hands.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Types of Yards Rescape AI Can Design for You</h2>
          <p className={articleBodyStyle}>
            Rescape AI, your personal AI backyard generator, opens up a world of
            possibilities for designing your yard. With a variety of styles at your
            fingertips, this AI tool integrates pathways, structures, and unique design
            elements tailored to your preferences and the natural landscape.
          </p>
          <p className={articleBodyStyle}>
            Let's explore just some of the captivating yard types that Rescape AI can
            bring to life:
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={largeImageGrid} style={{ marginTop: 0 }}>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Japanese Ornaments</h2>
              <img
                src="/articles/zen-japanese-ornaments.webp"
                alt="AI yard design for Japanese Zen aesthetic"
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                Rescape AI yard design for inspiring Zen yard design ideas featuring
                authentic Japanese ornaments, serene gravel patterns, and minimalist
                landscaping. Browse our{" "}
                <Link href="/garden-design-guide/zen/zen-garden-ideas-using-ai">
                  Zen Design ideas
                </Link>{" "}
                to create a peaceful outdoor sanctuary.
              </p>
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Patio and flowerbeds</h2>
              <img
                src="/articles/ai-yard-design-patio-and-flowerbeds.webp"
                alt="AI yard design with an interesting irregular patio and beautiful cast iron seating. Highly inspiring."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                Perfect for those who enjoy relaxed outdoor living, Rescape AI designs
                inviting patios surrounded by vibrant flowerbeds. Imagine lounging in a
                cozy seating area amidst a burst of colorful blooms and fragrant flowers,
                creating an atmosphere of warmth and charm.
              </p>
              <p className={imagePreviewBodyStyle}>
                This setup not only beautifies the space but also offers a versatile area,
                ideal for casual gatherings or a peaceful escape.
              </p>
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Contemporary Pegola</h2>
              <img
                src="/articles/contemporary-pegola.webp"
                alt="Contemporary Pegola Yard Design Idea"
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                Transform your outdoor space with a contemporary pergola surrounded by
                elegant landscaping. AI yard design helps you visualize modern outdoor
                living spaces that seamlessly blend comfort, style, and nature.
              </p>
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Cottage Gardens</h2>
              <img
                src="/articles/cottage-informal-and-diverse.webp"
                alt="Cottage small backyard full of lush plantings and diverse plant selection"
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                Create a charming cottage garden filled with informal, diverse plantings,
                colorful blooms, and winding pathways. Ai Yard Design helps you design
                natural, inviting landscapes bursting with seasonal beauty and timeless
                character.
              </p>
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Wildlife and Flower Garden</h2>
              <img
                src="/articles/ai-yard-design-wildflower.webp"
                alt="AI-generated biodiverse landscape design concept, showcasing various plant and garden elements."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                Encourage biodiversity with a garden{" "}
                <Link href="/article/nature-in-contemporary-design">
                  designed to attract wildlife
                </Link>
                . Rescape AI carefully selects a mix of flowering plants and native
                species that not only thrive but also support local birds and insects.
              </p>
            </div>
            <div className={imagePreview2}>
              <h2 className={imageGridTitle}>Contemporary Garden</h2>
              <img
                src="/articles/ai-yard-design-contemporary-minimalist-design.webp"
                alt="Sharp, smart layouts create an ordered garden aura, enhancing the clean, minimalist feel of contemporary design."
                style={{
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginTop: `${golden}rem`,
                }}
                loading="lazy"
              />
              <p className={imagePreviewBodyStyle}>
                For those who prefer a modern aesthetic with clean lines and a{" "}
                <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
                  minimalistic design
                </Link>
                , Rescape AI integrates sleek, modern landscaping elements like
                streamlined water features, sculptural plants, and striking lighting
                fixtures.
              </p>
              <p className={imagePreviewBodyStyle}>
                This style often features a palette of cool-toned plants and sharp
                geometric shapes, making your garden a statement piece of modern art.
              </p>
            </div>
          </div>
          <p className={articleBodyStyle} style={{ marginTop: `${golden * 2}rem` }}>
            Each design by Rescape AI not only reflects your personal style but also
            optimises the use of space, making{" "}
            <Link href="/article/back-garden-ideas">your backyard</Link> a true extension
            of your home. With such a tool at your disposal, every corner of your yard has
            the potential to become a bespoke paradise, tailored just for you.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style} id="how-it-works">
            How Does Rescape AI Yard Design Work?
          </h2>
          <ol className={orderedList}>
            <li>
              <b className={bold}>Take a Photo of Your Yard:</b> Begin with a snapshot to
              give the AI a canvas to assess the layout and potential of your space.
            </li>
            <li>
              <b className={bold}>Upload and Customise:</b> Upload the photo to Rescape AI
              and customise your yard by choosing a recommended style and decorative
              features. This will decide on the overall landscape style, from serene Zen
              gardens to vibrant floral paradises.
            </li>
            <li>
              <b className={bold}>Set the Creativity Level:</b> Adjust the AI&apos;s
              creativity level to fine-tune how much of your yard is transformed. Lower
              creativity settings make fewer changes, keeping much of your yard's original
              character, while a setting of 100% reimagines the space entirely. The
              default setting is 42%, which balances familiarity and innovation
              effectively.
            </li>
            <li>
              <b className={bold}>Explore AI-Generated Designs:</b> With your input and
              chosen creativity level, the AI crafts tailored design ideas, presenting
              multiple layouts and styles. These visual proposals allow you to see
              potential transformations of your yard, ensuring the final design feels
              uniquely yours.
            </li>
          </ol>
          <p
            className={articleBodyStyle}
            style={{ textAlign: "center", marginTop: `${golden}rem` }}
          >
            <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
              Read our tutorial
            </Link>{" "}
            or <Link href="/hello/signup">Join now</Link>
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>
            AI Yard Design with Rescape: Unleashing Creativity in Every Corner
          </h2>
          <p className={articleBodyStyle}>
            At the heart of Rescape AI lies a commitment to bringing professional garden
            design within everyone's reach. This innovative technology is like a personal
            landscape architect readily available in your browser.
          </p>
          <p className={articleBodyStyle}>
            It opens up new avenues for homeowners, empowering them to reimagine their
            outdoor spaces with ease and enjoyment.
          </p>
          <p className={articleBodyStyle}>
            The advantage of integrating artificial intelligence in yard design is that it
            invites an interactive experience where creativity can flourish without the
            limitations or intimidations of traditional landscaping processes.
          </p>
          <p className={articleBodyStyle}>
            The pathway towards achieving that dream garden can begin today. Respace AI is
            all about <Link href="/hello/signup">giving life to your ideas</Link> right
            here, right now – effortlessly guiding you through each tap and swipe toward
            creating a green haven at home.
          </p>
        </section>
        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Get started now
          </LinkButton>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default Index
